import { Callout, Colors, Icon, Pills } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ObjectivePeriodFields from './ObjectivePeriodFields';
import { useFormErrorMessage } from '../../hooks/useFormErrorMessage';
import ObjectiveEmployeePicker from '../ObjectiveEmployeePicker';
import { GlassFrame, WhitePanel } from '../surfaces';

import type { ObjectiveFormData } from '../../validation';
import type { ObjectiveEmployeePickerProps } from '../ObjectiveEmployeePicker';
import type { ComponentProps } from 'react';
import type { Control } from 'react-hook-form';

type FieldBoundPickerProps = 'selectedEmployeeIds' | 'onChange' | 'error' | 'errorMessage';

interface ObjectiveAssigneesCardProps {
    control: Control<ObjectiveFormData>;
    /** Shows the advice on pairing complementary people, above the people it is about. */
    isTeam: boolean;
    employeePicker: Omit<ObjectiveEmployeePickerProps, FieldBoundPickerProps>;
    period: Omit<ComponentProps<typeof ObjectivePeriodFields>, 'control'>;
}

/** Who the objective is for, and the period it runs over. */
export default function ObjectiveAssigneesCard({
    control,
    isTeam,
    employeePicker,
    period,
}: ObjectiveAssigneesCardProps) {
    const { t } = useTranslation();
    const translateError = useFormErrorMessage();

    return (
        <GlassFrame dataTestId={'objective-form-header'}>
            <WhitePanel>
                <div className={'flex flex-col gap-[48px]'}>
                    {isTeam && (
                        <Callout
                            surface={'gradient'}
                            badge={
                                <Pills
                                    variant={'frosted'}
                                    size={'frosted'}
                                >
                                    <Icon
                                        name={'users-people'}
                                        size={'sm'}
                                        color={Colors.PRIMARY}
                                        ariaHidden
                                    />
                                    {t('objectives.form.teamTipBadge')}
                                </Pills>
                            }
                            dataTestId={'objective-form-team-tip'}
                        >
                            {t('objectives.form.teamTipText')}
                        </Callout>
                    )}

                    <Controller
                        name={'employeeIds'}
                        control={control}
                        render={({ field, fieldState }) => (
                            <ObjectiveEmployeePicker
                                {...employeePicker}
                                selectedEmployeeIds={field.value}
                                onChange={field.onChange}
                                error={!!fieldState.error}
                                errorMessage={translateError(fieldState.error?.message)}
                            />
                        )}
                    />

                    <ObjectivePeriodFields
                        control={control}
                        {...period}
                    />
                </div>
            </WhitePanel>
        </GlassFrame>
    );
}
